import React from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { AddressTypes } from '@garnish/location';
import { useNavbarContainerHeight } from '@garnish/navigation';
import { Modal, ModalRow } from '@garnish/overlays';

import { Map } from '@sg/radar-fe';

import { getEnvVars } from '@order/utils';

import { useWindowWidthAtLeast } from '../../../../hooks';
import { DeliveryLocationCard } from '../DeliveryLocationCard';
import { RestaurantLocationCard } from '../RestaurantLocationCard';

import type { ConfirmLocationLayoutProps } from './ConfirmLocationLayout.types';

export const ConfirmLocationLayoutXS = (props: ConfirmLocationLayoutProps) => {
  const { deliveryAddress, orderType, pins, region, restaurant } = props;

  const { formatMessage } = useIntl();
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const hasRoomForCenteredText = useWindowWidthAtLeast({ minWidth: CENTERED_TEXT_MIN_WINDOW_WIDTH }); // prettier-ignore

  // ─── Derived ─────────────────────────────────────────────────────────

  const isDelivery = orderType === AddressTypes.delivery;

  // ─── Styles ──────────────────────────────────────────────────────────

  const scrollContentContainerStyles = [styles.scrollContentContainer, navbarPaddingBottomStyle];
  const centeredTextStyle = hasRoomForCenteredText ? styles.centeredText : undefined;
  const headlineStyles = [styles.headline, centeredTextStyle];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView
      testID="confirm-location.container-xs"
      style={styles.container}
      contentContainerStyle={scrollContentContainerStyles}
    >
      <Modal.Headline style={headlineStyles}>
        {formatMessage(messages.locationConfirmLocation, { channel: orderType })}
      </Modal.Headline>

      <View style={styles.contentContainer}>
        <ModalRow style={styles.mapRow}>
          <View style={styles.mapContainer}>
            <Map publishableKey={RADAR_SDK_KEY} region={region} pins={pins} />
          </View>
        </ModalRow>

        <ModalRow style={styles.locationContainer}>
          {isDelivery && deliveryAddress ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard orderType={orderType} restaurant={restaurant} />
          )}
        </ModalRow>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  scrollContentContainer: {
    flexGrow: 1,
    paddingTop: theme.spacing['6'],
  },
  contentContainer: {
    flex: 1,
  },
  headline: {
    marginBottom: theme.spacing['4'],
  },
  centeredText: {
    textAlign: 'center',
  },
  mapContainer: {
    flex: 1,
    borderRadius: theme.radius.medium,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    overflow: 'hidden',
  },
  mapRow: {
    flex: 0.5,
    minHeight: 192,
  },
  locationContainer: {
    paddingVertical: theme.spacing['4'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

const CENTERED_TEXT_MIN_WINDOW_WIDTH = 500;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly locationConfirmLocation: { readonly channel: MessageValue };
}>({
  locationConfirmLocation: {
    defaultMessage: 'Confirm {channel} location',
    description: 'Location | Confirm Location',
  },
});
